import { useEffect, useMemo, useRef, useState } from 'react'
import type { Map as MapboxMap } from 'mapbox-gl'
import { Loader2, Map as MapIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/shared/api/client'
import { RankedBars } from '@/shared/ui/charts'
import { hasMapbox, loadMapbox, styleUrl, KENYA_BOUNDS } from './mapbox'
import { extractRings, ringBounds } from './geo'

/**
 * Many units, one value each, painted on the map.
 *
 * `UnitMap` draws ONE boundary or MANY points. This draws many boundaries with a
 * value scale, which is a different question and a different set of failure
 * modes — so it is a separate component rather than a fourth mode on that one.
 *
 * ## The join is on unit id, never on name
 *
 * `values` is keyed by `unitId`, and the geometry comes from
 * `GET /geography/features`, whose every feature carries its own `id`. Two wards
 * can share a name, one ward can be spelled three ways in a register, and a
 * fuzzy name match paints the wrong ward — which is a mistake nobody catches by
 * looking at a map. A unit with no value is drawn in the "no data" fill and is
 * counted in the caption; it is never drawn as zero.
 *
 * ## No token is a supported state
 *
 * Falls back to a ranked bar list of exactly the same numbers. That is the rule
 * everywhere in this app, and here it matters more than usual: an advisory
 * screen that shows nothing when a Mapbox token is rotated badly looks like the
 * analysis failed.
 */

export interface ChoroplethValue {
  unitId: string
  label: string
  value: number | null
}

interface FeatureCollection {
  type: string
  features: {
    type: string
    geometry: unknown
    properties: Record<string, unknown>
  }[]
  properties?: Record<string, unknown>
}

/** Sequential ramp, light to dark, single hue. Ordered data gets an ordered scale. */
const RAMP = ['#f5ede4', '#e2cbb0', '#cba77c', '#ad7f4c', '#8a5a28', '#5e3a12']
const NO_DATA = '#d4d4d8'

export function ChoroplethMap({
  unitType,
  parentUnitId,
  values,
  valueLabel,
  onSelect,
  className,
  height = 'h-[420px]',
}: {
  unitType: string
  parentUnitId?: string | null
  values: ChoroplethValue[]
  valueLabel: string
  onSelect?: (unitId: string) => void
  className?: string
  height?: string
}) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapboxMap | null>(null)
  const pick = useRef(onSelect)
  pick.current = onSelect

  const [features, setFeatures] = useState<FeatureCollection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>(
    hasMapbox() ? 'loading' : 'idle')
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))

  const byUnit = useMemo(() => {
    const map = new Map<string, ChoroplethValue>()
    values.forEach((v) => map.set(v.unitId, v))
    return map
  }, [values])

  const scale = useMemo(() => {
    const numbers = values.map((v) => v.value).filter((v): v is number => v != null)
    if (numbers.length === 0) return null
    return { min: Math.min(...numbers), max: Math.max(...numbers) }
  }, [values])

  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])

  // ── geometry ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setError(null)
    const query = new URLSearchParams({ type: unitType, cap: '2000' })
    if (parentUnitId) query.set('parentId', parentUnitId)
    api.get<FeatureCollection>(`/geography/features?${query.toString()}`)
      .then((fc) => { if (!cancelled) setFeatures(fc) })
      .catch(() => { if (!cancelled) setError('Boundaries could not be loaded.') })
    return () => { cancelled = true }
  }, [unitType, parentUnitId])

  // ── map ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasMapbox() || !host.current) return
    let cancelled = false
    setStatus('loading')
    loadMapbox().then((mapboxgl) => {
      if (cancelled || !host.current) return
      const m = new mapboxgl.Map({
        container: host.current,
        style: styleUrl('theme', dark),
        bounds: KENYA_BOUNDS,
        fitBoundsOptions: { padding: 16 },
      })
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
      m.on('load', () => { if (!cancelled) setStatus('ready') })
      m.on('error', () => { if (!cancelled) setStatus('failed') })
      map.current = m
    }).catch(() => { if (!cancelled) setStatus('failed') })
    return () => {
      cancelled = true
      map.current?.remove()
      map.current = null
    }
    // Once. Theme changes are applied by the effect below; recreating the map
    // would flash and lose the viewport.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = host.current
    if (!el || status !== 'ready') return
    const obs = new ResizeObserver(() => map.current?.resize())
    obs.observe(el)
    return () => obs.disconnect()
  }, [status])

  // ── paint ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status !== 'ready' || !features) return

    const joined = {
      type: 'FeatureCollection',
      features: features.features.map((f) => {
        const id = String(f.properties?.id ?? '')
        const hit = byUnit.get(id)
        return {
          ...f,
          properties: {
            ...f.properties,
            // `hasValue` is a separate flag rather than relying on value === null:
            // mapbox expressions treat a missing property and a zero identically in
            // an interpolate, and "nobody counted" must not paint as "zero".
            value: hit?.value ?? 0,
            hasValue: hit && hit.value != null ? 1 : 0,
            valueLabel: hit?.label ?? String(f.properties?.name ?? ''),
          },
        }
      }),
    }

    const min = scale?.min ?? 0
    const max = scale?.max ?? 1
    const span = max - min || 1
    const stops: (number | string)[] = []
    RAMP.forEach((colour, i) => {
      stops.push(min + (span * i) / (RAMP.length - 1), colour)
    })

    const source = m.getSource('advisory-choropleth')
    if (source) {
      ;(source as { setData: (d: unknown) => void }).setData(joined)
      return
    }
    m.addSource('advisory-choropleth', { type: 'geojson', data: joined as never })
    m.addLayer({
      id: 'advisory-choropleth-fill',
      type: 'fill',
      source: 'advisory-choropleth',
      paint: {
        'fill-color': [
          'case',
          ['==', ['get', 'hasValue'], 0], NO_DATA,
          ['interpolate', ['linear'], ['get', 'value'], ...stops],
        ] as never,
        'fill-opacity': ['case', ['==', ['get', 'hasValue'], 0], 0.35, 0.78] as never,
      },
    })
    m.addLayer({
      id: 'advisory-choropleth-line',
      type: 'line',
      source: 'advisory-choropleth',
      paint: { 'line-color': '#3f3f46', 'line-width': 0.6, 'line-opacity': 0.5 },
    })

    m.on('click', 'advisory-choropleth-fill', (event) => {
      // `GeoJSONFeature` in mapbox-gl's types does not declare `properties`, so the
      // read is widened rather than cast to `any` — the shape is ours, set a few
      // lines above when the source data was built.
      const hit = event.features?.[0] as { properties?: Record<string, unknown> } | undefined
      const id = hit?.properties?.id
      if (id) pick.current?.(String(id))
    })
    m.on('mouseenter', 'advisory-choropleth-fill', () => {
      m.getCanvas().style.cursor = 'pointer'
    })
    m.on('mouseleave', 'advisory-choropleth-fill', () => {
      m.getCanvas().style.cursor = ''
    })

    const bounds = ringBounds(features.features.flatMap(
      (f) => extractRings({ type: 'Feature', geometry: f.geometry } as never)))
    if (bounds) {
      m.fitBounds([[bounds.minLon, bounds.minLat], [bounds.maxLon, bounds.maxLat]],
        { padding: 24, duration: 0, maxZoom: 11 })
    }
  }, [features, byUnit, scale, status])

  const withData = values.filter((v) => v.value != null).length
  const missing = (features?.features.length ?? 0) - withData

  const caption = (
    <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-erp-xs text-muted-foreground">
      <span>{valueLabel}</span>
      <span className="flex items-center gap-2">
        {scale && (
          <>
            <span className="tabular-nums">{scale.min.toLocaleString()}</span>
            <span className="flex h-2 w-24 overflow-hidden rounded-sm">
              {RAMP.map((c) => (
                <span key={c} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </span>
            <span className="tabular-nums">{scale.max.toLocaleString()}</span>
          </>
        )}
        {missing > 0 && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-4 rounded-sm" style={{ backgroundColor: NO_DATA }} />
            {missing} with no data
          </span>
        )}
      </span>
    </div>
  )

  // No token, or the map failed: the same numbers, ranked.
  if (!hasMapbox() || status === 'failed' || error) {
    return (
      <div className={className}>
        <RankedBars
          points={values
            .filter((v) => v.value != null)
            .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
            .slice(0, 20)
            .map((v) => ({ label: v.label, value: v.value ?? 0 }))}
          emptyMessage="No unit in this scope carried a value."
        />
        <div className="mt-1.5 flex items-center gap-1.5 text-erp-xs text-muted-foreground">
          <MapIcon className="h-3 w-3" />
          {error ?? 'No map is configured for this deployment — the same values, ranked.'}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className={cn('relative overflow-hidden rounded-md border border-border', height)}>
        <div ref={host} className="absolute inset-0" />
        {(status === 'loading' || !features) && (
          <div className="absolute inset-0 grid place-items-center bg-background/60">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {caption}
    </div>
  )
}
